import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useLocation } from 'react-router-dom';
import { CART_EVENTS } from '@eshop/utils/cartEvents.ts';

type DrawerCloser = () => void;

interface CheckoutLayoutContextValue {
  /** True when the shopper is traversing checkout routes or a guard forces the mode. */
  checkoutMode: boolean;
  /** Helper to know when header affordances should remain hidden (i.e. no staff bypass). */
  hideNavigation: boolean;
  /** Whether staff has explicitly re-enabled navigation while staying in checkout. */
  staffBypassEnabled: boolean;
  /** Allow staff to toggle navigation visibility during checkout (noop for guests). */
  setStaffBypassEnabled: Dispatch<SetStateAction<boolean>>;
  /** Guard API so checkout pages can opt-in even if the pathname changes mid-flow. */
  enterCheckoutGuard: () => void;
  leaveCheckoutGuard: () => void;
  /** Allow UI components (cart drawer, floating panels) to register closing callbacks. */
  registerDrawerCloser: (closer: DrawerCloser) => () => void;
  /** Immediately requests any registered drawers to close (and fires the legacy event). */
  requestCloseCartDrawer: () => void;
}

const noop = () => undefined;
const CheckoutLayoutContext = createContext<CheckoutLayoutContextValue>({
  checkoutMode: false,
  hideNavigation: false,
  staffBypassEnabled: false,
  setStaffBypassEnabled: noop,
  enterCheckoutGuard: noop,
  leaveCheckoutGuard: noop,
  registerDrawerCloser: () => noop,
  requestCloseCartDrawer: noop,
});

const CHECKOUT_PATH_PREFIX = '/eshop/checkout';

export const CheckoutLayoutProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const [manualDepth, setManualDepth] = useState(0);
  const [staffBypassEnabled, setStaffBypassEnabled] = useState(false);
  const drawerClosersRef = useRef<Set<DrawerCloser>>(new Set());

  const isCheckoutRoute = pathname.startsWith(CHECKOUT_PATH_PREFIX);

  // If navigation occurs away from checkout entirely, reset manual guards.
  useEffect(() => {
    if (!isCheckoutRoute && manualDepth !== 0) {
      setManualDepth(0);
    }
  }, [isCheckoutRoute]);

  const enterCheckoutGuard = useCallback(() => {
    setManualDepth(depth => depth + 1);
  }, []);

  const leaveCheckoutGuard = useCallback(() => {
    setManualDepth(depth => (depth > 0 ? depth - 1 : 0));
  }, []);

  const checkoutMode = isCheckoutRoute || manualDepth > 0;
  const hideNavigation = checkoutMode && !staffBypassEnabled;

  const registerDrawerCloser = useCallback((closer: DrawerCloser) => {
    drawerClosersRef.current.add(closer);
    return () => {
      drawerClosersRef.current.delete(closer);
    };
  }, []);

  const requestCloseCartDrawer = useCallback(() => {
    drawerClosersRef.current.forEach(closer => {
      try {
        closer();
      } catch (error) {
        // Ignored: a failing closer should not break other subscribers.
        console.warn('checkoutLayout: failed to close registered drawer', error);
      }
    });

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(CART_EVENTS.CLOSE_DRAWER));
    }
  }, []);

  useEffect(() => {
    if (!checkoutMode && staffBypassEnabled) {
      setStaffBypassEnabled(false);
    }
  }, [checkoutMode, staffBypassEnabled]);

  useEffect(() => {
    if (hideNavigation) {
      requestCloseCartDrawer();
    }
  }, [hideNavigation, requestCloseCartDrawer]);

  const value = useMemo<CheckoutLayoutContextValue>(() => ({
    checkoutMode,
    hideNavigation,
    staffBypassEnabled,
    setStaffBypassEnabled,
    enterCheckoutGuard,
    leaveCheckoutGuard,
    registerDrawerCloser,
    requestCloseCartDrawer,
  }), [
    checkoutMode,
    hideNavigation,
    staffBypassEnabled,
    enterCheckoutGuard,
    leaveCheckoutGuard,
    registerDrawerCloser,
    requestCloseCartDrawer,
  ]);

  return (
    <CheckoutLayoutContext.Provider value={value}>
      {children}
    </CheckoutLayoutContext.Provider>
  );
};

export const useCheckoutLayout = () => useContext(CheckoutLayoutContext);
